<script setup lang="ts">
// The setup's draft so far, and — once there is one — the confirm card (v3 `OrpheusSetupCard.vue`,
// KUR-1/KUR-8). The confirm button is the only door that saves what Orpheus prepared, exactly as
// it was previewed. A row changes through the conversation instead: the edit goes back as one
// chat turn and brings a new card, while this card's button waits.
import { computed, nextTick, ref } from 'vue'
import { RouterLink } from 'vue-router'

import { canRetryDraft, storedDraftState, useSetupDrafts } from '../../composables/useSetupDrafts'
import { useSetupLabels } from '../../composables/useSetupLabels'
import type { SetupCard, SetupSlot } from '../../models/orpheusSetup.types'
import { handOffAlertDraft } from '../../service/alertDraftHandoff'
import {
  alertTimingSentence,
  cardCostLabel,
  cardRows,
  cardTitle,
  confirmedSummary,
  confirmLabel,
  existingAlertNames,
  formLink,
  incompleteCard,
  rowEditMessage,
  TRY_AGAIN_MESSAGE,
  type CardRowView,
} from '../../service/orpheusSetupCardPresentation'

import { useWorkspace } from '@/features/account'
import { useI18n } from '@/shared/i18n'
import { Button } from '@/shared/ui'

const props = withDefaults(
  defineProps<{
    /** The confirm card; null while the setup has no draft yet and only its fields so far show. */
    card?: SetupCard | null
    slots?: SetupSlot[]
    /** Only the thread's latest card can be saved: a newer preview supersedes every earlier one. */
    active?: boolean
    loading?: boolean
  }>(),
  { card: null, slots: () => [], active: true, loading: false },
)
// A message for the conversation: "Try again", or a row the user changed.
const emit = defineEmits<{ send: [text: string] }>()

const { t } = useI18n()
const labels = useSetupLabels()
const { workspaceId } = useWorkspace()
const { draftState, confirmDraft, discardDraft } = useSetupDrafts()
const K = 'orpheus.setup'

const state = computed(
  () => draftState(props.card?.draftRef) ?? storedDraftState(props.card?.draftStatus),
)
const incomplete = computed(() => (props.card ? null : incompleteCard(labels, props.slots)))
const rows = computed(() =>
  props.card ? cardRows(labels, props.card, props.slots) : (incomplete.value?.rows ?? []),
)
const title = computed(() => (props.card ? cardTitle(labels, props.card) : t(`${K}.inProgress`)))
const tag = computed(() => {
  if (!props.card) return t(`${K}.tag.incomplete`)
  if (state.value?.status === 'saved') return t(`${K}.tag.saved`)
  if (state.value?.status === 'discarded') return t(`${K}.tag.discarded`)
  if (state.value?.failure?.kind === 'superseded' || (!props.active && !state.value)) {
    return t(`${K}.tag.replaced`)
  }
  if (state.value?.status === 'failed') return t(`${K}.tag.notSaved`)
  return t(`${K}.tag.ready`)
})
const costLabel = computed(() => (props.card ? cardCostLabel(labels, props.card) : ''))
const confirmText = computed(() => (props.card ? confirmLabel(labels, props.card) : ''))
const summary = computed(() =>
  props.card
    ? confirmedSummary(labels, props.card, state.value?.result ?? null)
    : { title: '', delivery: '', link: null },
)
const existing = computed(() => (props.card ? existingAlertNames(props.card) : ''))
const timingSentence = computed(() => (props.card ? alertTimingSentence(labels, props.card) : ''))
const openInForm = computed(() => (props.card ? formLink(props.card) : null))
// Refused or spent after the backend claimed it: only a fresh preview can be saved now.
const spent = computed(
  () =>
    state.value?.status === 'failed' &&
    !canRetryDraft(state.value) &&
    state.value.failure?.kind !== 'superseded',
)

const editingKey = ref('')
const editingValue = ref('')
const editorInput = ref<HTMLInputElement[] | null>(null)
const canEditRows = computed(() => Boolean(props.card) && props.active && !state.value)

function failureMessage(): string {
  const failure = state.value?.failure
  if (!failure) return ''
  return failure.message || t(failure.messageKey)
}

function startEditing(row: CardRowView): void {
  editingKey.value = row.key
  editingValue.value = row.value.chips.length
    ? row.value.chips.join(', ')
    : row.value.plain
      ? ''
      : row.value.text
  void nextTick(() => editorInput.value?.[0]?.focus())
}

function finishEditing(row: CardRowView): void {
  const value = editingValue.value.trim()
  if (!value) return
  editingKey.value = ''
  emit('send', rowEditMessage(row.label, value))
}

const canConfirm = computed(() => {
  if (!props.card || !props.active || props.loading || editingKey.value) return false
  return !state.value || canRetryDraft(state.value)
})

function discard(): void {
  if (props.card) discardDraft(props.card.draftRef)
}

function confirm(): void {
  const workspace = workspaceId.value
  if (!canConfirm.value || !props.card || workspace === undefined) return
  void confirmDraft(workspace, props.card.draftRef)
}

function handOff(): void {
  handOffAlertDraft(props.card?.formValues)
}
</script>

<template>
  <div
    class="orpheus-setup-card w-full overflow-hidden rounded-xl border border-border bg-surface"
    data-testid="orpheus-setup-card"
  >
    <div class="flex items-baseline justify-between gap-2 px-4 py-2.5">
      <span
        class="min-w-0 wrap-anywhere text-sm font-bold text-primary"
        data-testid="orpheus-setup-card-title"
      >
        {{ title }}
      </span>
      <span class="shrink-0 text-xs text-muted" data-testid="orpheus-setup-card-tag">
        {{ tag }}
      </span>
    </div>

    <template v-for="row in rows" :key="row.key">
      <div
        class="grid grid-cols-1 gap-1 border-t border-border px-4 py-2.5 sm:grid-cols-[8.25rem_minmax(0,1fr)_auto] sm:gap-3"
        data-testid="orpheus-setup-card-row"
      >
        <div class="text-xs text-muted">{{ row.label }}</div>
        <div
          class="flex min-w-0 flex-wrap items-center gap-1.5 wrap-anywhere text-sm"
          :class="
            row.value.plain || row.source === 'missing'
              ? 'font-normal text-muted'
              : 'font-semibold text-primary'
          "
        >
          <template v-if="row.value.chips.length">
            <span
              v-for="(chip, chipIndex) in row.value.chips"
              :key="chipIndex"
              class="rounded-sm border border-border bg-surface-sunken px-2 py-0.5 text-sm font-normal text-fg"
            >
              {{ chip }}
            </span>
          </template>
          <template v-else>{{ row.value.text }}</template>
          <span
            v-if="row.source === 'guess'"
            class="text-sm font-normal text-muted"
            data-testid="orpheus-setup-row-source"
          >
            {{ t(`${K}.source.guess`) }}
          </span>
          <span
            v-else-if="row.source === 'missing'"
            class="text-sm font-normal text-muted"
            data-testid="orpheus-setup-row-source"
          >
            {{ t(`${K}.source.missing`) }}
          </span>
        </div>
        <button
          v-if="canEditRows"
          type="button"
          :disabled="Boolean(editingKey) || loading"
          class="cursor-pointer justify-self-start border-0 bg-transparent p-0 text-xs font-semibold text-info hover:underline disabled:cursor-not-allowed disabled:opacity-50 sm:justify-self-end"
          data-testid="orpheus-setup-row-edit"
          @click="startEditing(row)"
        >
          {{ t(`${K}.action.edit`) }}
        </button>
      </div>
      <form
        v-if="editingKey === row.key"
        class="border-t border-border bg-info-soft px-4 py-3.5"
        data-testid="orpheus-setup-row-editor"
        @submit.prevent="finishEditing(row)"
      >
        <label :for="`setup-row-${row.key}`" class="mb-2 block text-xs text-muted">
          {{ row.label }}
        </label>
        <input
          :id="`setup-row-${row.key}`"
          ref="editorInput"
          v-model="editingValue"
          type="text"
          class="w-full rounded-md border border-orpheus-muted bg-surface px-3 py-2 text-sm font-semibold text-primary focus:border-primary-muted focus:outline-none"
        />
        <p class="mt-2 mb-0 text-xs text-muted">{{ t(`${K}.editHint`) }}</p>
        <div class="mt-2.5 flex items-center gap-3">
          <Button
            type="submit"
            size="sm"
            :disabled="!editingValue.trim()"
            data-testid="orpheus-setup-row-done"
          >
            {{ t(`${K}.action.done`) }}
          </Button>
          <button
            type="button"
            class="cursor-pointer border-0 bg-transparent p-0 text-sm font-semibold text-muted hover:text-primary hover:underline"
            @click="editingKey = ''"
          >
            {{ t('orpheus.consentSettings.cancel') }}
          </button>
        </div>
      </form>
    </template>

    <p
      v-if="incomplete?.stillNeeded.length"
      class="m-0 border-t border-border px-4 py-2.5 text-sm text-muted"
      data-testid="orpheus-setup-card-still-needed"
    >
      {{ t(`${K}.stillNeeded`, { fields: incomplete.stillNeeded.join(', ') }) }}
    </p>

    <div v-if="card" class="flex flex-col gap-3 border-t border-border px-4 py-3.5 text-sm">
      <p
        v-if="costLabel || card.irreversible"
        class="m-0 text-fg"
        data-testid="orpheus-setup-card-cost"
      >
        <template v-if="costLabel">{{ t(`${K}.cost.charged`, { cost: costLabel }) }}</template>
        <template v-if="card.irreversible"> {{ t(`${K}.cost.irreversible`) }}</template>
      </p>
      <p v-if="existing" class="m-0 text-muted" data-testid="orpheus-setup-card-existing">
        {{ t(`${K}.existing`, { names: existing }) }}
      </p>
      <p
        v-if="timingSentence && state?.status !== 'discarded'"
        class="m-0 text-fg"
        data-testid="orpheus-setup-card-timing"
      >
        {{ timingSentence }}
      </p>

      <div
        v-if="state?.status === 'saved'"
        class="flex flex-col gap-1 text-fg"
        data-testid="orpheus-setup-card-saved"
      >
        <p class="m-0 font-semibold text-primary">{{ summary.title }}</p>
        <p v-if="summary.delivery" class="m-0">{{ summary.delivery }}</p>
        <p v-if="state.failure" class="m-0 text-muted">{{ failureMessage() }}</p>
        <RouterLink
          v-if="summary.link"
          :to="summary.link.to"
          class="self-start font-semibold text-info hover:underline"
        >
          {{ summary.link.label }}
        </RouterLink>
      </div>

      <p
        v-else-if="state?.status === 'discarded'"
        class="m-0 text-muted"
        data-testid="orpheus-setup-card-discarded"
      >
        {{ t(`${K}.discarded`) }}
      </p>

      <template v-else>
        <div
          v-if="state?.status === 'failed' && state.failure"
          class="flex flex-col gap-1 text-fg"
          data-testid="orpheus-setup-card-failure"
        >
          <p class="m-0">{{ failureMessage() }}</p>
          <ul v-if="state.failure.rowErrors.length" class="m-0 list-disc pl-5 text-muted">
            <li
              v-for="rowError in state.failure.rowErrors"
              :key="`${rowError.field}-${rowError.code}`"
            >
              {{ rowError.message }}
            </li>
          </ul>
        </div>
        <p v-else-if="!active" class="m-0 text-muted" data-testid="orpheus-setup-card-replaced">
          {{ t(`${K}.replaced`) }}
        </p>

        <div class="flex flex-wrap items-center gap-2.5">
          <Button
            v-if="spent"
            size="sm"
            :disabled="loading"
            data-testid="orpheus-setup-try-again"
            @click="emit('send', TRY_AGAIN_MESSAGE)"
          >
            {{ t(`${K}.action.tryAgain`) }}
          </Button>
          <Button
            v-else
            size="sm"
            :disabled="!canConfirm"
            :loading="state?.status === 'confirming'"
            data-testid="orpheus-setup-confirm"
            @click="confirm"
          >
            {{ state?.status === 'confirming' ? t(`${K}.action.saving`) : confirmText }}
          </Button>
          <RouterLink
            v-if="openInForm && (active || spent)"
            :to="openInForm"
            class="text-sm font-semibold text-muted hover:text-primary hover:underline"
            data-testid="orpheus-setup-open-in-form"
            @click="handOff"
          >
            {{ t(`${K}.action.openInForm`) }}
          </RouterLink>
          <span class="flex-1" />
          <button
            v-if="active && !state"
            type="button"
            class="cursor-pointer border-0 bg-transparent p-0 text-sm font-semibold text-muted hover:text-primary hover:underline"
            data-testid="orpheus-setup-discard"
            @click="discard"
          >
            {{ t(`${K}.action.discard`) }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>
